import React from 'react';
import { Link } from 'react-router-dom';

/**
 * A deliberately tiny markdown renderer for assistant replies.
 *
 * It builds React elements — never dangerouslySetInnerHTML. Model output is
 * untrusted text arriving over the network, so the renderer is the boundary: if a
 * construct is not handled here it renders as literal characters, which is the
 * failure mode we want.
 *
 * Handles: paragraphs, bullet and numbered lists, **bold**, `code`, [text](href),
 * and bare URLs. Everything else is text.
 */

// Order matters: markdown links are matched before the bare-path pattern, so the
// href inside [label](/path) is never mistaken for a loose path in prose.
const INLINE =
  /(\*\*[^*\n]+\*\*|`[^`\n]+`|\[[^\]\n]+\]\([^)\s]+\)|https?:\/\/[^\s<>()]+|\/[a-z0-9]+(?:[/-][a-z0-9]+)*)/gi;

const linkClass =
  'text-moss-light underline decoration-moss-deep underline-offset-2 ' +
  'hover:text-moss hover:decoration-moss transition-colors';

/**
 * Render one link. Internal paths become client-side routes so the visitor keeps
 * the open chat panel across the navigation instead of a full page reload — but
 * only when the path is one the site actually has (allowedPaths comes from the
 * server). An unknown path renders as plain text rather than a dead route.
 */
function renderLink(href, label, key, ctx) {
  const { allowedPaths, linkLabels, onNavigate } = ctx;

  if (/^https?:\/\//i.test(href)) {
    return (
      <a key={key} href={href} target="_blank" rel="noopener noreferrer" className={linkClass}>
        {label}
      </a>
    );
  }

  if (href.startsWith('/')) {
    const path = href.split(/[?#]/)[0].replace(/\/$/, '') || '/';
    if (!allowedPaths || allowedPaths.length === 0 || allowedPaths.includes(path)) {
      // A label that is just the path ("[/contact](/contact)") reads like a bug.
      // Swap in the canonical human label when we have one.
      const text = label.startsWith('/') && linkLabels?.[path] ? linkLabels[path] : label;
      return (
        <Link key={key} to={href} className={linkClass} onClick={onNavigate}>
          {text}
        </Link>
      );
    }
  }

  return <span key={key}>{label}</span>;
}

/** Split one line into bold / code / link / url / path / text spans. */
function renderInline(line, keyPrefix, ctx) {
  const parts = [];
  let last = 0;
  let match;

  INLINE.lastIndex = 0;
  while ((match = INLINE.exec(line)) !== null) {
    if (match.index > last) parts.push(line.slice(last, match.index));
    const token = match[0];
    const key = `${keyPrefix}-${match.index}`;

    if (token.startsWith('**')) {
      parts.push(
        <strong key={key} className="font-semibold text-silver-light">
          {token.slice(2, -2)}
        </strong>
      );
    } else if (token.startsWith('`')) {
      parts.push(
        <code
          key={key}
          className="rounded bg-black/60 px-1 py-0.5 font-mono text-[0.85em] text-moss-light"
        >
          {token.slice(1, -1)}
        </code>
      );
    } else if (token.startsWith('[')) {
      const split = token.indexOf('](');
      parts.push(renderLink(token.slice(split + 2, -1), token.slice(1, split), key, ctx));
    } else if (token.startsWith('/')) {
      // A bare site path the model dropped into prose. Only linkify it if it is a
      // real page — otherwise it is a date, a fraction, or "and/or", and must stay
      // literal text.
      const path = token.replace(/\/$/, '');
      const label = ctx.linkLabels?.[path];
      if (label && ctx.allowedPaths?.includes(path)) {
        parts.push(renderLink(path, label, key, ctx));
      } else {
        parts.push(token);
      }
    } else {
      // Bare URL. Trailing punctuation belongs to the sentence, not the link.
      const trimmed = token.replace(/[.,;:!?)]+$/, '');
      parts.push(
        renderLink(trimmed, trimmed.replace(/^https?:\/\//, ''), key, { ...ctx, allowedPaths: null })
      );
      if (trimmed.length < token.length) parts.push(token.slice(trimmed.length));
    }

    last = match.index + token.length;
  }

  if (last < line.length) parts.push(line.slice(last));
  return parts.length ? parts : [line];
}

/**
 * @param {string} text            raw assistant text (may be mid-stream)
 * @param {object} [opts]
 * @param {string[]} [opts.allowedPaths] internal paths that may become routes
 * @param {Function} [opts.onNavigate]   called when a link is followed
 * @returns {React.ReactNode[]}
 */
export function renderRichText(text, opts = {}) {
  const ctx = {
    allowedPaths: opts.allowedPaths,
    linkLabels: opts.linkLabels,
    onNavigate: opts.onNavigate,
  };
  const lines = String(text || '').split('\n');

  const blocks = [];
  let list = null; // { ordered: boolean, items: string[] }

  const flushList = () => {
    if (!list) return;
    const Tag = list.ordered ? 'ol' : 'ul';
    blocks.push(
      <Tag
        key={`l${blocks.length}`}
        className={`my-1.5 space-y-1 pl-4 ${list.ordered ? 'list-decimal' : 'list-disc'} marker:text-moss`}
      >
        {list.items.map((item, i) => (
          <li key={i} className="pl-0.5">
            {renderInline(item, `l${blocks.length}-${i}`, ctx)}
          </li>
        ))}
      </Tag>
    );
    list = null;
  };

  lines.forEach((raw, idx) => {
    const line = raw.replace(/^#{1,6}\s+/, '').trimEnd();

    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);

    if (bullet || numbered) {
      const ordered = Boolean(numbered);
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, items: [] };
      }
      list.items.push((bullet || numbered)[1]);
      return;
    }

    flushList();
    if (!line.trim()) return;

    blocks.push(
      <p key={`p${idx}`} className="whitespace-pre-wrap">
        {renderInline(line, `p${idx}`, ctx)}
      </p>
    );
  });

  flushList();
  return blocks;
}

export default renderRichText;
